class PDPProductOverviewDetails extends HTMLElement {
  constructor() {
    super();
    // Bind the setAddToCartDisabledAttribute method to the instance.
    Shopify.ProductOverviewSetAddToCartDisabledAttribute =
      this.setAddToCartDisabledAttribute.bind(this);
    Shopify.ProductOverviewUpdateVariantPrice =
      this.updateVariantPrice.bind(this);
    Shopify.ProductOverviewShowOutStockKlavyoButton =
      this.showOutOfStockKlavyoButton.bind(this);
  }

  // Lifecycle method called when the element is inserted into the DOM.
  connectedCallback() {
    this.section = this;
    this.addToCartBtn = this.querySelector("#pdp-product-overview-add-to-bag");
    this.quantityInput = this.querySelector(
      `pdp-product-overview-quantity-input input[type="number"]`
    );
    this.sellingPlan = this.querySelector("#pdp-product-overview-selling-plan");

    this.addToCartBtnHandlerBound = this.addToCartBtnHandler.bind(this);
    this.addToCartBtn?.addEventListener("click", this.addToCartBtnHandlerBound);

    this.variantPriceUpdateListener =
      this.handleVariantPriceEvent.bind(this);
    document.addEventListener(
      "pdp:variant-price-update",
      this.variantPriceUpdateListener
    );
  }

  // Lifecycle method called when the element is removed from the DOM.
  disconnectedCallback() {
    this.addToCartBtn?.removeEventListener(
      "click",
      this.addToCartBtnHandlerBound
    );
    if (this.variantPriceUpdateListener) {
      document.removeEventListener(
        "pdp:variant-price-update",
        this.variantPriceUpdateListener
      );
    }
  }

  /**
   * Public API
   *
   * Sets the 'disabled' attribute of the 'Add to Cart' button based on the provided flag.
   * @param {string} flag - The flag to determine whether the button should be disabled.
   */
  setAddToCartDisabledAttribute(flag) {
    const isSoldOut = flag === true || flag === "true";
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = isSoldOut;
    }
  }

  updateVariantPrice(payload) {
    if (!this.addToCartBtn) return;
    const { price, compareAtPrice } = this.normalizePricePayload(payload);
    if (typeof price !== "number" || Number.isNaN(price)) return;

    const formattedPrice = this.formatPrice(price);
    const regularPriceEl = this.addToCartBtn.querySelector(
      ".price__regular .price-item--regular"
    );
    const salePriceEl = this.addToCartBtn.querySelector(
      ".price__sale .price-item--sale"
    );
    const comparePriceEl = this.addToCartBtn.querySelector(
      ".price__compare .price-item--regular"
    );
    const priceWrapper = this.addToCartBtn.querySelector(".price");

    if (regularPriceEl) regularPriceEl.innerHTML = formattedPrice;
    if (salePriceEl) salePriceEl.innerHTML = formattedPrice;

    const hasComparePrice = this.hasValidComparePrice(compareAtPrice, price);

    if (comparePriceEl) {
      if (hasComparePrice) {
        comparePriceEl.innerHTML = this.formatPrice(compareAtPrice);
      } else {
        comparePriceEl.innerHTML = "";
      }
    }

    if (priceWrapper) {
      priceWrapper.classList.toggle("price--on-sale", hasComparePrice);
    }

    this.updateComparePriceWidget(price, compareAtPrice, hasComparePrice);
  }

  /**
   * Handles global variant price events so ATC prices update outside the selling plan module.
   * @param {CustomEvent} event
   */
  handleVariantPriceEvent(event) {
    const priceInCents = this.normalizePriceValue(event?.detail?.price);
    if (priceInCents === null) return;
    const compareAtInCents = this.normalizePriceValue(
      event?.detail?.compareAtPrice
    );
    this.updateVariantPrice({
      price: priceInCents / 100,
      compareAtPrice:
        compareAtInCents !== null ? compareAtInCents / 100 : null,
    });
  }

  /**
   * Normalizes the payload accepted by updateVariantPrice.
   * @param {number|{price:number|string,compareAtPrice?:number|string}} payload
   * @returns {{price: number|null, compareAtPrice: number|null}}
   */
  normalizePricePayload(payload) {
    if (typeof payload === "number") {
      return { price: payload, compareAtPrice: null };
    }
    if (payload && typeof payload === "object") {
      return {
        price: this.normalizePriceValue(payload.price),
        compareAtPrice: this.normalizePriceValue(payload.compareAtPrice),
      };
    }
    return { price: null, compareAtPrice: null };
  }

  /**
   * Parses numeric values safely.
   * @param {number|string|null|undefined} value
   * @returns {number|null}
   */
  normalizePriceValue(value) {
    if (value === null || value === undefined || value === "") return null;
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  /**
   * Formats a price using Shopify's money helper when available.
   * @param {number} value
   * @returns {string}
   */
  formatPrice(value) {
    const cents = Math.round(value * 100);
    if (
      typeof Shopify !== "undefined" &&
      typeof Shopify.formatMoney === "function"
    ) {
      const formatted = Shopify.formatMoney(
        cents,
        window?.theme?.moneyFormat || "${{amount}}"
      );
      return formatted.replace(".00", "");
    }

    const dollars = (cents / 100).toFixed(2);
    return dollars.endsWith(".00")
      ? `$${dollars.slice(0, -3)}`
      : `$${dollars}`;
  }

  /**
   * Updates the standalone compare-price widget block, if present.
   * @param {number} price
   * @param {number|null} compareAtPrice
   * @param {boolean} hasComparePrice
   */
  updateComparePriceWidget(price, compareAtPrice, hasComparePrice) {
    const widget = this.section?.querySelector("[data-price-widget]");
    if (!widget) return;

    const priceEl = widget.querySelector(".price-widget--price");
    const compareEl = widget.querySelector(".price-widget--compare-at-price");
    const savingsEl = widget.querySelector(".price-widget--savings");
    const savingsValueEl = widget.querySelector(".price-widget--savings-value");

    if (priceEl) priceEl.innerHTML = this.formatPrice(price);

    if (!hasComparePrice) {
      widget.hidden = true;
      if (compareEl) compareEl.innerHTML = "";
      if (savingsEl) savingsEl.hidden = true;
      if (savingsValueEl) savingsValueEl.innerHTML = "";
      return;
    }

    widget.hidden = false;
    if (compareEl) compareEl.innerHTML = this.formatPrice(compareAtPrice);
    if (savingsEl) savingsEl.hidden = false;
    if (savingsValueEl) {
      const savings = (compareAtPrice || 0) - price;
      savingsValueEl.innerHTML = this.formatPrice(savings);
    }
  }

  /**
   * Determines whether a compare-at price should be displayed.
   * @param {number|null} compareAtPrice
   * @param {number} price
   * @returns {boolean}
   */
  hasValidComparePrice(compareAtPrice, price) {
    return (
      typeof compareAtPrice === "number" &&
      !Number.isNaN(compareAtPrice) &&
      compareAtPrice > price
    );
  }

  showOutOfStockKlavyoButton(variantIsSoldOut) {
    const isSoldOut =
      variantIsSoldOut === true || variantIsSoldOut === "true";
    const btn = this.querySelector(".klaviyo-bis-trigger");
    // Ensure the button exists before trying to manipulate it.
    if (!btn) return;
    if (isSoldOut) {
      btn.classList.add("show-klaviyo-out-of-stock-btn");
      btn.classList.remove("hide-klaviyo-out-of-stock-btn");
    } else {
      btn.classList.add("hide-klaviyo-out-of-stock-btn");
      btn.classList.remove("show-klaviyo-out-of-stock-btn");
    }
  }

  /**
   * Event handler for the 'Add to Cart' button click.
   */
  addToCartBtnHandler() {
    const productVariantSelector = this.section.querySelector(
      ".product-variant-selector"
    );
    const { productVariantSelectorType } = productVariantSelector.dataset;

    // Dispatch to the appropriate controller based on the selector type.
    switch (productVariantSelectorType) {
      case "default-layout-selector":
        this.defaultLayoutSelectorController(productVariantSelector);
        break;
      case "images-as-swatches":
        this.imagesAsSwatchesController(productVariantSelector);
        break;
      case "swatch-as-link":
        this.swatchAsLinkController(productVariantSelector);
        break;
      case "link-dropdown-selector":
        this.linkDropdownSelectorController(productVariantSelector);
        break;
      case "default-dropdown-selector":
        this.defaultDropdownSelectorController(productVariantSelector);
        break;
      case "hidden-input":
        this.hiddenInputController(productVariantSelector);
        break;
      default:
        console.error(
          `❌ PDPProductOverviewDetails | addToCartBtnHandler | Selector type unknown: ${productVariantSelectorType}`
        );
        break;
    }
  }

  /**
   * Controller for default layout variant selector.
   */
  defaultLayoutSelectorController(productVariantSelector) {
    const variantSelector = productVariantSelector.querySelector(
      `[data-variant-selected="true"]`
    );
    const { variantId } = variantSelector.dataset;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Controller for images as swatches variant selector.
   */
  imagesAsSwatchesController(productVariantSelector) {
    const variantSelector = productVariantSelector.querySelector(
      `[data-swatch-selected="true"]`
    );
    const { variantId } = variantSelector.dataset;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Controller for swatch as link variant selector.
   */
  swatchAsLinkController(productVariantSelector) {
    const variantSelector = productVariantSelector.querySelector(
      `[data-swatch-link-selected="true"]`
    );
    const { variantId } = variantSelector.dataset;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Controller for the linked-product dropdown selector.
   */
  linkDropdownSelectorController(productVariantSelector) {
    const variantSelector = productVariantSelector.querySelector(
      "select option:checked"
    );
    if (!variantSelector) return;
    const { variantId } = variantSelector.dataset;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Controller for default dropdown variant selector.
   */
  defaultDropdownSelectorController(productVariantSelector) {
    const dropdown = productVariantSelector.querySelector(
      "#product-variant-default-dropdown-selector"
    );

    const value = dropdown.value;
    const { variantId } = dropdown.querySelector(
      `option[value="${value}"]`
    ).dataset;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Controller for hidden input variant selector.
   */
  hiddenInputController(productVariantSelector) {
    const variantId = productVariantSelector.value;
    const sellingPlanId = this.getSellingPlanId();
    this.addToCart(variantId, this.quantityInput.value, sellingPlanId);
  }

  /**
   * Gets the selling plan ID based on the currently selected selling plan.
   * @returns {boolean|string} Returns the selling plan ID or false if not found.
   */
  getSellingPlanId() {
    if (!this.sellingPlan) return false;
    const selectedItem = this.sellingPlan.querySelector(
      `.selling-plan-item[data-selling-plan-active="true"]`
    );
    const { sellingPlan } = selectedItem.dataset;
    if (sellingPlan === "one-time") return false;

    return this.sellingPlan.querySelector("#selling-plans-dropdown").value;
  }

  /**
   * Adds the selected product variant to the cart.
   * @param {string} variantId - The ID of the selected variant.
   * @param {string} quantity - The quantity selected by the user.
   */
  async addToCart(variantId, quantity, sellingPlan) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
          ...(sellingPlan && { selling_plan: sellingPlan }),
        },
      ],
    };

    try {
      await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
    } catch (error) {
      console.log(`❌ PDPProductOverviewDetails | addToCart | ${error}`);
    }
  }
}

customElements.define(
  "pdp-product-overview--details",
  PDPProductOverviewDetails
);
